import { Fragment, useState } from "react";
import makeStyles from "styles/muiStylesCompat/makeStyles";
import { useTranslation } from "react-i18next";
import { useSnackbar } from "notistack";

import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import TextField from "@mui/material/TextField";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import ToggleButton from "@mui/material/ToggleButton";
import ButtonPrimary from "components/Buttons/ButtonPrimary";

import MyTextField from "components/Inputs/MyTextField";
import { saveDriver } from "~/services/http/drivers";
import {
  driverInviteEmail2Schema,
  driverInviteEmailSchema,
  driverOptionalPhoneSchema,
} from "~/validation/contactFields";

const useStyles = makeStyles((theme) => ({
  leftColumn: { paddingRight: theme.spacing(4) },
  grow: { flexGrow: 1 },
  textField: {
    width: "100%",
  },
  toggleButton: {},
  avatar: {
    margin: 16,
    color: "#bbb",
    textAlign: "center",
    width: 160,
    height: 160,
    backgroundColor: "#eee",
    borderWidth: "2px",
    borderStyle: "dashed",
    borderColor: "#bbb",
  },
  toggleButtonSelected: {
    color: "#fff" + " !important",
    backgroundColor: theme.palette.primary.main + " !important",
  },
}));

const DriverForm = (props) => {
  const { enqueueSnackbar } = useSnackbar();
  const [inputErrors, setInputErrors] = useState({});

  const { values, updateValues, fields, myCompany, myUser } = props;
  const { t } = useTranslation();
  const classes = useStyles();

  const includeEmail = !fields || fields.includes("email");

  const handleDriverType = (_event, newType) => {
    updateValues({ status: newType });
  };

  const handleFormChange = ({ target }) => {
    let { value, name } = target;

    if (["email", "email2"].indexOf(name) > -1) {
      value = value.toLowerCase();
    }
    setInputErrors((prev) => ({ ...prev, [name]: undefined }));
    updateValues({ [target.name]: value });
  };

  const handleConfirmUser = () => {
    const request = {
      params: { drivers: true, company: myCompany?.id },
      data: {
        id: values.id,
        verified: new Date().toISOString().replace(/\.\d{3}Z$/, "Z"),
      },
    };

    saveDriver(request).then(
      (response) => {
        updateValues(response);
        enqueueSnackbar("Tallennettu", {
          variant: "success",
          anchorOrigin: { vertical: "bottom", horizontal: "center" },
          autoHideDuration: 2000,
        });
      },
      (_errors) => {
        enqueueSnackbar("Tallennus epäonnistui", {
          variant: "error",
          anchorOrigin: { vertical: "bottom", horizontal: "center" },
          autoHideDuration: 2000,
        });
      },
    );
  };

  const validatePhoneBlur = (raw) => {
    try {
      driverOptionalPhoneSchema().validateSync(raw);
      setInputErrors((prev) => ({ ...prev, phone: undefined }));
    } catch (e) {
      setInputErrors((prev) => ({ ...prev, phone: e.message }));
    }
  };

  const validateEmailBlur = (raw) => {
    if (!includeEmail || values?.id) {
      return;
    }
    try {
      driverInviteEmailSchema().validateSync(raw);
      setInputErrors((prev) => ({ ...prev, email: undefined }));
    } catch (e) {
      setInputErrors((prev) => ({ ...prev, email: e.message }));
    }
  };

  const validateEmail2Blur = (raw) => {
    if (!includeEmail || values?.id) {
      return;
    }
    try {
      driverInviteEmail2Schema(values?.email).validateSync(raw);
      setInputErrors((prev) => ({ ...prev, email2: undefined }));
    } catch (e) {
      setInputErrors((prev) => ({ ...prev, email2: e.message }));
    }
  };

  return (
    <Fragment>
      <Grid container spacing={1}>
        <Grid size={{ xs: 12 }}>
          <Grid container direction="row" spacing={1}>
            {!fields || fields.includes("name") ? (
              <Fragment>
                <Grid size={{ xs: 12, sm: 5 }}>
                  <MyTextField
                    autoFocus
                    customLabel={t("Etunimi")}
                    id="driver-first_name"
                    name="first_name"
                    value={values.first_name || ""}
                    onChange={handleFormChange}
                    required={false}
                  />
                </Grid>

                <Grid size={{ xs: 12, sm: 1 }}></Grid>

                <Grid size={{ xs: 12, sm: 6 }}>
                  <MyTextField
                    customLabel={t("Sukunimi")}
                    id="driver-last_name"
                    name="last_name"
                    value={values.last_name || ""}
                    onChange={handleFormChange}
                    required={false}
                  />
                </Grid>
              </Fragment>
            ) : null}

            {includeEmail ? (
              <Fragment>
                <Grid size={{ xs: 12 }}>
                  <Typography variant="subtitle1">Sähköposti</Typography>

                  <TextField
                    className={classes.textField}
                    id="driver-email"
                    fullWidth
                    margin="dense"
                    name="email"
                    onChange={handleFormChange}
                    onBlur={(e) => validateEmailBlur(e.target.value)}
                    required={!values.id}
                    disabled={Boolean(values.id)}
                    slotProps={{ inputLabel: { shrink: true } }}
                    type="email"
                    value={values.email || ""}
                    variant="outlined"
                    error={Boolean(inputErrors.email)}
                    helperText={inputErrors.email || undefined}
                  />
                </Grid>

                {!values.id ? (
                  <Grid size={{ xs: 12 }}>
                    <Typography variant="subtitle1">
                      Sähköposti uudelleen
                    </Typography>

                    <TextField
                      className={classes.textField}
                      id="driver-email2"
                      fullWidth
                      margin="dense"
                      name="email2"
                      onChange={handleFormChange}
                      onBlur={(e) => validateEmail2Blur(e.target.value)}
                      required={!values.id}
                      disabled={Boolean(values.id)}
                      slotProps={{ inputLabel: { shrink: true } }}
                      type="email"
                      value={values.email2 || ""}
                      variant="outlined"
                      error={Boolean(inputErrors.email2)}
                      helperText={inputErrors.email2 || undefined}
                    />
                  </Grid>
                ) : null}
              </Fragment>
            ) : null}

            <Grid size={{ xs: 12 }}>
              <Typography variant="subtitle1">Puhelinnumero</Typography>

              <TextField
                className={classes.textField}
                fullWidth
                helperText={
                  inputErrors.phone || "Käytä vain kansainvälistä muotoa"
                }
                id="driver-phone"
                margin="dense"
                name="phone"
                onChange={handleFormChange}
                onBlur={(e) => validatePhoneBlur(e.target.value)}
                required={false}
                slotProps={{ inputLabel: { shrink: true } }}
                placeholder="+358"
                value={values.phone || ""}
                variant="outlined"
                error={Boolean(inputErrors.phone)}
              />
            </Grid>

            {(!fields || fields.includes("status")) && myUser?.status == 100 ? (
              <Fragment>
                <Grid size={{ xs: 12 }}>
                  <Box sx={{ mt: "16px" }}>
                    <ToggleButtonGroup
                      value={values.status || 1}
                      exclusive
                      onChange={handleDriverType}
                      aria-label="text alignment"
                    >
                      <ToggleButton
                        classes={{ selected: classes.toggleButtonSelected }}
                        className={classes.toggleButton}
                        value={1}
                        aria-label="left aligned"
                      >
                        Peruskuljettaja
                      </ToggleButton>
                      <ToggleButton
                        classes={{ selected: classes.toggleButtonSelected }}
                        className={classes.toggleButton}
                        value={10}
                        aria-label="right aligned"
                      >
                        Superkuljettaja
                      </ToggleButton>
                      <ToggleButton
                        classes={{ selected: classes.toggleButtonSelected }}
                        className={classes.toggleButton}
                        value={100}
                        aria-label="right aligned"
                      >
                        Admin
                      </ToggleButton>
                    </ToggleButtonGroup>
                  </Box>
                </Grid>
              </Fragment>
            ) : null}

            {(!fields || fields.includes("verified")) &&
            values.id &&
            myUser?.status == 100 ? (
              <Fragment>
                {values.verified ? (
                  <Grid size={{ xs: 12 }}>
                    <Box sx={{ mt: "16px" }}>
                      <Typography variant="subtitle1">
                        Käyttäjätili on vahvistettu
                      </Typography>
                    </Box>
                  </Grid>
                ) : (
                  <Fragment>
                    <Grid size={{ xs: 12 }}>
                      <Box sx={{ mt: "16px" }}>
                        <Typography variant="subtitle1">
                          Käyttäjätiliä ei ole vielä vahvistettu
                        </Typography>
                      </Box>
                    </Grid>

                    <Grid size={{ xs: 12 }}>
                      <ButtonPrimary onClick={handleConfirmUser}>
                        {t("Vahvista käyttäjätili tästä")}
                      </ButtonPrimary>
                    </Grid>
                  </Fragment>
                )}
              </Fragment>
            ) : null}
          </Grid>
        </Grid>
      </Grid>
    </Fragment>
  );
};

DriverForm.defaultProps = { fields: null };

export default DriverForm;
